"use client";

import { useState, useRef, useEffect, useImperativeHandle, forwardRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Check, ArrowLeft, ArrowRight } from "lucide-react";
import {
  type AccessibilityStatement,
  defaultStatement,
} from "../types/statement";
import { useLocalStorage } from "@/hooks/use-local-storage";
import { getStatement, saveStatement } from "@/services/statement";
import {
  BasicInformationStep,
  basicInfoSchema,
} from "./steps/basic-information-step";
import { YourEffortsStep } from "./steps/your-efforts-step";
import {
  ConformanceStatusStep,
  conformanceStatusSchema,
} from "./steps/conformance-status-step";
import { TechnicalSpecificationsStep } from "./steps/technical-specifications-step";
import { FeedbackStep } from "./steps/feedback-step";
import { RelatedEvidenceStep } from "./steps/related-evidence-step";
import { SuccessScreen } from "./success-screen";

const steps = [
  { id: "basic", label: "Basic information", number: 1 },
  { id: "efforts", label: "Your efforts", number: 2 },
  { id: "conformance", label: "Conformance Status", number: 3 },
  { id: "technical", label: "Technical specifications", number: 4 },
  { id: "feedback", label: "Feedback options", number: 5 },
];

interface AccessibilityStatementGeneratorProps {
  onSuccessScreenChange?: (show: boolean) => void;
}

export interface AccessibilityStatementGeneratorRef {
  handlePreview: () => Promise<void>;
}

export const AccessibilityStatementGenerator = forwardRef<AccessibilityStatementGeneratorRef, AccessibilityStatementGeneratorProps>(
  ({ onSuccessScreenChange }, ref) => {
  const previewRef = useRef<HTMLDivElement>(null);
  const hasLoadedRef = useRef(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [storage, setStorage] = useLocalStorage<AccessibilityStatement>(
    "accessibility-statement",
    defaultStatement
  );
  const statement = storage as AccessibilityStatement;
  const [showSuccessScreen, setShowSuccessScreen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [basicInfoErrors, setBasicInfoErrors] = useState<{
    companyName?: string;
    websiteUrl?: string;
    websiteName?: string;
  } | null>(null);
  const [conformanceStatusErrors, setConformanceStatusErrors] = useState<{
    standard?: string;
    conformanceLevel?: string;
  } | null>(null);

  // Notify parent component when success screen state changes
  useEffect(() => {
    onSuccessScreenChange?.(showSuccessScreen);
  }, [showSuccessScreen, onSuccessScreenChange]);

  // Handle preview functionality
  const handlePreview = useCallback(async () => {
    try {
      const response = await saveStatement(statement, { preview: true });
      const savedStatement = response as AccessibilityStatement;
      setStorage(savedStatement);
      
      // Open the preview URL
      if (savedStatement?.preview) {
        window.open(savedStatement.preview, '_blank');
      }
    } catch (error) {
      console.error("Failed to save statement for preview:", error);
    }
  }, [statement, setStorage]);

  // Expose handlePreview to parent component
  useImperativeHandle(ref, () => ({
    handlePreview
  }), [handlePreview]);

  // Load data from API on initial load
  useEffect(() => {
    if (hasLoadedRef.current) return;

    const loadStatementData = async () => {
      try {
        setIsLoading(true);
        if (statement?.status === "published") {
          setShowSuccessScreen(true);
        }
        if (
          statement.status === "in_progress" ||
          statement.status === "published"
        )
          return;
        const response = await getStatement();
        if (response && typeof response === "object") {
          const apiData = response as AccessibilityStatement;
          apiData.status = "in_progress";
          setStorage((prev) => ({
            ...prev,
            ...apiData, 
          }));
        }
      } catch (error) {
        console.error("Failed to load statement data:", error);
        // Keep using local storage data if API fails
      } finally {
        setIsLoading(false);
        hasLoadedRef.current = true;
      }
    };

    loadStatementData();
  }, []); // Empty dependency array to run only once

  const updateStatement = (updates: Partial<AccessibilityStatement>) => {
    setStorage((prev) => ({
      ...prev,
      ...updates,
    }));
  };

  // Map form fields/steps to preview section IDs
  const fieldToSection: Record<string, string> = {
    // Basic Info
    companyName: "preview-basic",
    website: "preview-basic",
    publicationDate: "preview-basic",
    // Efforts
    selectedEfforts: "preview-efforts",
    additionalEfforts: "preview-efforts",
    // Conformance
    standard: "preview-conformance",
    conformanceLevel: "preview-conformance",
    customStandard: "preview-conformance",
    additionalConsiderations: "preview-conformance",
    knownIssues: "preview-conformance",
    unresolvedBarriers: "preview-conformance",
    contentOutsideScope: "preview-conformance",
    // Technical
    knownCompatibilities: "preview-technical",
    knownIncompatibilities: "preview-technical",
    technologyReliance: "preview-technical",
    assessmentApproach: "preview-technical",
    // Feedback
    companyPhoneNumber: "preview-feedback",
    companyEmailAddress: "preview-feedback",
    companyVisitorAddress: "preview-feedback",
    companyPostalAddress: "preview-feedback",
    sameAsVisitorAddress: "preview-feedback",
    otherContactOptions: "preview-feedback",
    typicalDurationForResponse: "preview-feedback",
    enforcementTeam: "preview-feedback",
    // Evidence
    recentEvaluationReport: "preview-evidence",
    evaluationStatement: "preview-evidence",
    otherEvidence: "preview-evidence",
  };

  // Scroll preview to section if not in viewport
  function scrollPreviewToSection(sectionId: string) {
    if (!previewRef.current) return;
    const section = previewRef.current.querySelector(`#${sectionId}`);
    if (section) {
      const rect = section.getBoundingClientRect();
      const previewRect = previewRef.current.getBoundingClientRect();
      // Check if section is in preview viewport
      if (rect.top < previewRect.top || rect.bottom > previewRect.bottom) {
        section.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  }

  const nextStep = () => {
    if (currentStep === 0) {
      const result = basicInfoSchema.safeParse(statement.basicInfo);
      if (!result.success) {
        // Collect all field errors
        const fieldErrors: {
          companyName?: string;
          websiteUrl?: string;
          websiteName?: string;
        } = {};
        result.error.errors.forEach((err) => {
          if (err.path[0] === "companyName")
            fieldErrors.companyName = err.message;
          if (err.path[0] === "websiteUrl")
            fieldErrors.websiteUrl = err.message;
          if (err.path[0] === "websiteName")
            fieldErrors.websiteName = err.message;
        });
        setBasicInfoErrors(fieldErrors);
        // Scroll to first error
        const order = ["companyName", "websiteUrl", "websiteName"];
        for (const key of order) {
          if (fieldErrors[key as keyof typeof fieldErrors]) {
            const el = document.getElementById(key);
            if (el) {
              const y = el.getBoundingClientRect().top + window.scrollY - 110;
              window.scrollTo({ top: y, behavior: "smooth" });
              (el as HTMLElement).focus?.();
            }
            break;
          }
        }
        return;
      } else {
        setBasicInfoErrors(null);
      }
    }
    if (currentStep === 2) {
      const result = conformanceStatusSchema.safeParse(
        statement.conformanceStatus
      );
      if (!result.success) {
        const fieldErrors: { standard?: string; conformanceLevel?: string } =
          {};
        result.error.errors.forEach((err) => {
          if (err.path[0] === "standard") fieldErrors.standard = err.message;
          if (err.path[0] === "conformanceLevel")
            fieldErrors.conformanceLevel = err.message;
        });
        setConformanceStatusErrors(fieldErrors);
        // Scroll to first error
        const order = ["standard", "conformanceLevel"];
        for (const key of order) {
          if (fieldErrors[key as keyof typeof fieldErrors]) {
            const el = document.getElementById(key);
            if (el) {
              el.scrollIntoView({ behavior: "smooth", block: "center" });
              (el as HTMLElement).focus?.();
            }
            break;
          }
        }
        return;
      } else {
        setConformanceStatusErrors(null);
      }
    }
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
      const el = document.getElementById("wysg-statement-generator");
      if (el) {
        const y = el.getBoundingClientRect().top + window.scrollY - 110;
        window.scrollTo({ top: y, behavior: "smooth" });
      }
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      const el = document.getElementById("wysg-statement-generator");
      if (el) {
        const y = el.getBoundingClientRect().top + window.scrollY - 110;
        window.scrollTo({ top: y, behavior: "smooth" });
      }
    }
  };

  const goToStep = (stepIndex: number) => {
    setCurrentStep(stepIndex);
  };

  // Show loading state while fetching data
  if (isLoading) {
    return (
      <div className="cy:flex cy:items-center cy:justify-center cy:min-h-[400px]">
        <div className="cy:text-center">
          <div className="cy:animate-spin cy:rounded-full cy:h-12 cy:w-12 cy:border-b-2 cy:border-primary cy:mx-auto cy:mb-4"></div>
          <p className="cy:text-gray-600">
            Loading accessibility statement data...
          </p>
        </div>
      </div>
    );
  }

  // Show success screen after generation
  if (showSuccessScreen) {
    return (
      <SuccessScreen 
        setShowSuccessScreen={setShowSuccessScreen}
        setCurrentStep={setCurrentStep}
        setStorage={setStorage}
        statement={statement}
      />
    );
  }

  return (
    <div className="cy:bg-white" id="wysg-statement-generator">
      <div className="cy:overflow-hidden">
        <div className="cy:grid cy:grid-cols-12 cy:gap-6">
          <div className="wysg-form-container cy:col-span-10 cy:pt-10 cy:lg:py-10">
            <div className="wysg-form-container">
              <div className="cy:flex cy:items-center wysg-form-steps cy:px-4 cy:lg:px-10 cy:justify-between">
                {steps.map((step, index) => (
                  <div
                    key={step.id}
                    className="cy:flex cy:items-center cy:flex-1"
                  >
                    <Button
                      variant={
                        index === currentStep
                          ? "default"
                          : index < currentStep
                            ? "secondary"
                            : "outline"
                      }
                      size="sm"
                      className={`cy:rounded-full! cy:font-medium cy:text-base cy:text-foreground! cy:flex-shrink-0 cy:flex-grow-0 cy:w-9 cy:h-9 cy:p-0 cy:relative ${
                        index === currentStep
                          ? "cy:md:w-auto cy:md:px-3 cy:md:bg-transparent cy:md:tex-base cy:text-white! cy:md:text-foreground! cy:md:border cy:md:border-primary! cy:md:hover:text-white!"
                          : index < currentStep
                            ? "cy:bg-primary cy:text-white! cy:hover:bg-primary"
                            : "cy:bg-transparent cy:text-gray-400 cy:border"
                      }`}
                      onClick={() => goToStep(index)}
                    >
                      {index === currentStep ? (
                        <>
                          <span className="cy:inline cy:md:hidden cy:font-semibold">
                            {step.number}
                          </span>
                          <span className="cy:hidden cy:md:inline cy:font-semibold">
                            {step.label}
                          </span>
                        </>
                      ) : (
                        step.number
                      )}
                    </Button>
                    {index < steps.length - 1 && (
                      <div
                        className={`cy:h-px cy:mx-1 cy:flex-grow cy:min-w-4 cy:lg:min-w-10 ${
                          index < currentStep
                            ? "cy:bg-primary"
                            : "cy:bg-gray-300"
                        }`}
                      />
                    )}
                  </div>
                ))}
              </div>
              <div className="wysg-form-content cy:py-10 cy:px-4 cy:lg:px-10">
                {currentStep === 0 && (
                  <BasicInformationStep
                    data={statement.basicInfo}
                    onChange={(basicInfo, changedField) => {
                      updateStatement({ basicInfo });
                      if (changedField && fieldToSection[changedField]) {
                        scrollPreviewToSection(fieldToSection[changedField]);
                      }
                    }}
                    externalErrors={basicInfoErrors}
                  />
                )}
                {currentStep === 1 && (
                  <YourEffortsStep
                    data={statement.efforts}
                    onChange={(efforts, changedField) => {
                      updateStatement({ efforts });
                      if (changedField && fieldToSection[changedField]) {
                        scrollPreviewToSection(fieldToSection[changedField]);
                      }
                    }}
                  />
                )}
                {currentStep === 2 && (
                  <ConformanceStatusStep
                    data={statement.conformanceStatus}
                    onChange={(conformanceStatus, changedField) => {
                      updateStatement({ conformanceStatus });
                      if (changedField && fieldToSection[changedField]) {
                        scrollPreviewToSection(fieldToSection[changedField]);
                      }
                    }}
                    externalErrors={conformanceStatusErrors}
                  />
                )}
                {currentStep === 3 && (
                  <>
                    <TechnicalSpecificationsStep
                      data={statement.technicalSpecs}
                      onChange={(technicalSpecs, changedField) => {
                        updateStatement({ technicalSpecs });
                        if (changedField && fieldToSection[changedField]) {
                          scrollPreviewToSection(fieldToSection[changedField]);
                        }
                      }}
                    />
                    <RelatedEvidenceStep
                      data={statement.relatedEvidence}
                      onChange={(relatedEvidence, changedField) => {
                        updateStatement({ relatedEvidence });
                        if (changedField && fieldToSection[changedField]) {
                          scrollPreviewToSection(fieldToSection[changedField]);
                        }
                      }}
                    />
                  </>
                )}
                {currentStep === 4 && (
                  <FeedbackStep
                    data={statement.feedback}
                    onChange={(feedback, changedField) => {
                      updateStatement({ feedback });
                      if (changedField && fieldToSection[changedField]) {
                        scrollPreviewToSection(fieldToSection[changedField]);
                      }
                    }}
                    publicationDate={statement.basicInfo.publicationDate}
                    onPublicationDateChange={(date) => {
                      updateStatement({
                        basicInfo: {
                          ...statement.basicInfo,
                          publicationDate: date,
                        },
                      });
                    }}
                  />
                )}

                <div className="cy:flex cy:justify-start cy:mt-8 cy:gap-4">
                  {currentStep !== 0 && (
                    <Button
                      onClick={prevStep}
                      size={"lg"}
                      variant="outline"
                      className="cy:flex cy:items-center cy:gap-2 cy:py-4 cy:px-8! cy:rounded cy:text- cy:text-primary cy:font-semibold cy:border-primary"
                    >
                      <ArrowLeft className="cy:w-[24px]! cy:h-[24px]! cy:text-primary" />
                      Previous
                    </Button>
                  )}
                  {currentStep === steps.length - 1 ? (
                    <Button
                      type="button"
                      onClick={async () => {
                        try {
                          const updatedStatement = {
                            ...statement,
                            status: "published" as const,
                          };
                          const response =
                            await saveStatement(updatedStatement);
                          setStorage(response as AccessibilityStatement);
                          setShowSuccessScreen(true);
                        } catch (error) {
                          console.error("Failed to save statement:", error);
                          // Still show success screen even if save fails
                          setShowSuccessScreen(true);
                        }
                      }}
                      size={"lg"}
                      id="wysg-generate-btn"
                      className="cy:flex cy:items-center cy:gap-2 cy:py-4 cy:px-8! cy:rounded cy:text-base cy:font-semibold cy:bg-[#229D56]!"
                    >
                      <Check className="cy:w-[24px]! cy:h-[24px]!" />
                      Generate
                    </Button>
                  ) : (
                    <Button
                      onClick={nextStep}
                      disabled={currentStep === steps.length - 1}
                      size={"lg"}
                      className="cy:flex cy:items-center cy:gap-2 cy:py-4 cy:px-8! cy:rounded cy:text-base cy:font-semibold"
                    >
                      Next
                      <ArrowRight className="cy:w-[24px]! cy:h-[24px]!" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

AccessibilityStatementGenerator.displayName = 'AccessibilityStatementGenerator';

export default AccessibilityStatementGenerator;
